import React, { type PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

import { FlagsProvider } from '@atlaskit/flag';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { SpotlightManager } from '@atlaskit/onboarding';
import UFOSegment from '@atlaskit/react-ufo/segment';
import {
	startCapturingExtensions,
	stopCapturingExtensions,
} from '@atlassian/relay-capture-extensions';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import {
	createFeatureGateHandler,
	createFrontendExperimentHandler,
} from '@atlassian/search-experiment';

import ErrorDark from '../../common/constants/error/assets/error-dark.svg';
import ErrorLight from '../../common/constants/error/assets/error-light.svg';
import { SearchExperience } from '../../common/constants/search';
import { AppContextProvider, type AppContextProviderProps } from '../../common/ui/app-context';
import { darkImageKey, lightImageKey, manageImageCaching } from '../../common/ui/image-cache';
import MessagesIntlProvider from '../../common/ui/message-intl-provider/messages-intl-provider';
import { WidthObserverProvider } from '../../common/ui/width-observer';
import type { BootstrapContainerProps } from '../../controllers/store/bootstrap/bootstrap-store-provider';

import { Bootstrap } from './bootstrap';
import { SearchPageLayout } from './main';
import SearchResultsCqlQuery from './search-results/__generated__/searchResultsCqlQuery.graphql';
import SearchResultsQuery from './search-results/__generated__/searchResultsQuery.graphql';
import { SEARCH_SESSION_ID } from './utils/session';
import { FullPageSearchVitalStatsProvider } from './vital-stats-context';

type AppContextProviderPropsExposedThroughShell = Pick<
	AppContextProviderProps,
	'isNav4Enabled' | 'isAdminHubAIEnabled' | 'isRovoLLMEnabled'
>;

// Expose only the props that are needed at the root level.
type AppContextProviderPropsExposedThroughRootLevel = Pick<
	AppContextProviderProps,
	'mapUserIdToProfileUrl' | 'generateSearchUrl' | 'onNavigate' | 'onSearchResultsShown'
>;

type LayoutProps = {
	headerOffset?: number;
};

export type SearchPageProps = {
	shell: Omit<BootstrapContainerProps, 'features' | 'products'> &
		AppContextProviderPropsExposedThroughShell;
	callbacks?: BootstrapContainerProps['callbacks'];
} & AppContextProviderPropsExposedThroughRootLevel &
	LayoutProps;

/**
 * This is the main component which is exposed from the search-page
 * package.
 */
export const SearchPage = ({
	shell,
	callbacks,
	mapUserIdToProfileUrl,
	generateSearchUrl,
	onNavigate,
	onSearchResultsShown,
	headerOffset,
}: PropsWithChildren<SearchPageProps>): React.JSX.Element => {
	const { queryParams, isNav4Enabled, isAdminHubAIEnabled, isRovoLLMEnabled } = shell;

	const getFrontendExperiment = useMemo(() => createFrontendExperimentHandler(), []);
	const getFrontendGate = useMemo(() => createFeatureGateHandler(undefined), []);
	const [isOnline, _] = useState(navigator.onLine);
	useEffect(() => {
		manageImageCaching(isOnline, ErrorLight, lightImageKey, ErrorDark, darkImageKey);
	}, [isOnline]);

	// hold on to the first function reference passed to the package to
	// avoid excessive re-rendering if the consumer doesn't pass in
	// a stable function
	const mapUserIdToProfileUrlRef = useRef(mapUserIdToProfileUrl);

	// Start capturing extensions for SearchResultsQuery, SearchResultsCqlQuery
	useMemo(() => {
		startCapturingExtensions(SearchResultsQuery.operation.name);
		startCapturingExtensions(SearchResultsCqlQuery.operation.name);
	}, []);
	// Clear extensions for SearchResultsQuery, SearchResultsCqlQuery on unmount
	useEffect(
		() => () => {
			stopCapturingExtensions(SearchResultsQuery.operation.name);
			stopCapturingExtensions(SearchResultsCqlQuery.operation.name);
		},
		[],
	);

	return (
		<UFOSegment name="search-page-relay">
			<RelayEnvironmentProvider>
				<WidthObserverProvider>
					<SpotlightManager blanketIsTinted={false}>
						<Bootstrap {...shell} callbacks={callbacks} searchSessionId={SEARCH_SESSION_ID.value()}>
							<MessagesIntlProvider experience={SearchExperience.SEARCH_PAGE}>
								<AppContextProvider
									mapUserIdToProfileUrl={mapUserIdToProfileUrlRef.current}
									generateSearchUrl={generateSearchUrl}
									queryParams={queryParams}
									onNavigate={onNavigate}
									getFrontendExperiment={getFrontendExperiment}
									getFeatureGate={getFrontendGate}
									isNav4Enabled={isNav4Enabled}
									isAdminHubAIEnabled={isAdminHubAIEnabled}
									isRovoLLMEnabled={isRovoLLMEnabled}
									onSearchResultsShown={onSearchResultsShown}
								>
									<FlagsProvider>
										<SmartCardProvider>
											<FullPageSearchVitalStatsProvider>
												<SearchPageLayout
													headerOffset={headerOffset}
													aggAbsoluteUrl={shell.aggAbsoluteUrl}
												/>
											</FullPageSearchVitalStatsProvider>
										</SmartCardProvider>
									</FlagsProvider>
								</AppContextProvider>
							</MessagesIntlProvider>
						</Bootstrap>
					</SpotlightManager>
				</WidthObserverProvider>
			</RelayEnvironmentProvider>
		</UFOSegment>
	);
};
